import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, ContestTimer, Panel, SegmentedControl } from '@usacow/ds';
import { DrillHost, DRILL_LABEL, type DrillKind } from '@usacow/drills';
import { api, type ContestState } from '../lib/api';

type Division = 'bronze' | 'silver' | 'gold' | 'platinum';

export function Contest() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'setup' | 'live'>('setup');
  const [division, setDivision] = useState<Division>('silver');
  const [duration, setDuration] = useState('1h');
  const [state, setState] = useState<ContestState | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(0);
  const tick = useRef<number | null>(null);

  // Client countdown, re-synced from the authoritative server state on each refresh.
  useEffect(() => {
    if (phase !== 'live') return;
    tick.current = window.setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => {
      if (tick.current) window.clearInterval(tick.current);
    };
  }, [phase]);

  async function refresh() {
    const s = await api.contestState();
    setState(s);
    setRemaining(s.remainingSec);
    if (s.locked) {
      await api.contestLock().catch(() => {});
      navigate('/app/postmortem');
    }
  }

  async function start() {
    await api.contestStart(division, duration);
    setPhase('live');
    await refresh();
  }

  async function openProblem(id: string) {
    setActive(id);
    await api.contestEvent('open', id).catch(() => {});
  }

  async function lock() {
    await api.contestLock().catch(() => {});
    navigate('/app/postmortem');
  }

  if (phase === 'setup') {
    return (
      <div className="scr scr--narrow">
        <div className="scr-head">
          <div>
            <div className="t-eyebrow">contest simulation</div>
            <h1 className="scr-h">Set up a mock</h1>
          </div>
        </div>
        <Panel title="Division" style={{ marginBottom: 12 }}>
          <SegmentedControl<Division>
            value={division}
            onChange={setDivision}
            options={[
              { value: 'bronze', label: 'Bronze' },
              { value: 'silver', label: 'Silver' },
              { value: 'gold', label: 'Gold' },
              { value: 'platinum', label: 'Platinum' },
            ]}
          />
        </Panel>
        <Panel title="Duration" style={{ marginBottom: 16 }}>
          <SegmentedControl
            subtle
            value={duration}
            onChange={setDuration}
            options={[
              { value: '30m', label: '30m' },
              { value: '1h', label: '1h' },
              { value: '2h', label: '2h' },
            ]}
          />
          <ul className="cs-rules">
            <li>Hints disabled</li>
            <li>Server-authoritative timer</li>
            <li>Postmortem after lock</li>
          </ul>
        </Panel>
        <Button variant="primary" size="lg" onClick={start}>
          Start contest
        </Button>
      </div>
    );
  }

  if (!state) return <div className="route-loading">Loading…</div>;
  const cur = state.problems.find((p) => p.id === active);

  return (
    <div className="scr">
      <div className="ct-bar">
        <Badge tone={division} dot>
          {division[0]!.toUpperCase() + division.slice(1)} sim
        </Badge>
        <ContestTimer seconds={remaining} warnAt={300} critAt={60} label="to lock" />
        <Button variant="secondary" size="sm" onClick={lock}>
          Lock &amp; finish
        </Button>
      </div>

      <div className="ct-switch">
        {state.problems.map((p) => (
          <button
            key={p.id}
            className={`ct-p${active === p.id ? ' is-on' : ''}`}
            onClick={() => openProblem(p.id)}
          >
            <span className="mono">{p.id}</span>
            <Badge
              tone={
                p.status === 'submitted'
                  ? p.score && p.score >= 1000
                    ? 'correct'
                    : 'partial'
                  : 'neutral'
              }
              variant="outline"
            >
              {p.status === 'submitted' ? (p.score && p.score >= 1000 ? 'AC' : 'partial') : '—'}
            </Badge>
          </button>
        ))}
      </div>

      {cur ? (
        <Panel title={DRILL_LABEL[cur.drill as DrillKind]} meta={cur.id} style={{ marginTop: 14 }}>
          <DrillHost
            key={cur.id}
            kind={cur.drill as DrillKind}
            continueLabel="Submit"
            onComplete={async ({ grade }) => {
              await api.contestSubmit(cur.id, grade.score).catch(() => {});
              setActive(null);
              await refresh();
            }}
          />
        </Panel>
      ) : (
        <p className="dim" style={{ marginTop: 18 }}>
          Pick a problem above to start. Hints are disabled in contest mode.
        </p>
      )}
    </div>
  );
}
